import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Avatar } from "antd";
import { SaveOutlined, UserOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";

// APIs
import { activateUser, deactivateUser, updateUser, createUser } from "../../api/admin/userApi";
import { updateTeamMember, createTeamMember } from "../../api/admin/teamApi";
import { getAllRolesPractitioner } from "../../api/admin/rolesApi";
import { fetchClinicCompany } from "../../api/admin/companyApi";
import { fetchCompanyAvailableRolesPractitioner } from "../../api/admin/subscriptionApi";

// Types
import { User } from "../../types/admin/UserTypes";
import { Role } from "../../types/admin/RoleTypes";
import { TeamMember, defaultTeamMemberInfo } from "../../types/admin/TeamTypes";
import { isPractitionerColorConflictError, PRACTITIONER_COLOR_PALETTE_QUERY_KEY } from "../../types/admin/PractitionerColorTypes";

// Shared drawer
import DetailDrawer from "../shared/DetailDrawer";

// Tabs
import PersonalTab from "./tabs/PersonalTab";
import CompanyTab from "./tabs/CompanyTab";
import PreferencesTab from "./tabs/PreferencesTab";
import RolesTab from "./tabs/RolesTab";
import SampleTab from "./tabs/SampleTab";
import TeamMemberInfoTab from "./tabs/TeamMemberInfoTab";
import EducationTab from "./tabs/EducationTab";
import AvailabilityTab from "./tabs/AvailabilityTab";
import ResetPasswordModal from "./ResetPasswordModal";
import { hasFeature } from "../../utils/subscriptions/subscriptionService";

// ── Props ──────────────────────────────────────────────────────
export type EditUserModalMode = "admin" | "team";

export interface EditUserModalProps {
	isOpen: boolean;
	onClose: () => void;
	/** Pass User for admin mode, TeamMember for team mode */
	user: User | TeamMember;
	/** "admin" — full user management (activate/deactivate, company, samples)
	 *  "team" — team member management (team info, education, availability) */
	mode: EditUserModalMode;
	/** Called after a successful save, and also when the drawer is closed */
	onSaved: (user: User | TeamMember) => void;
}

// ── Helpers ────────────────────────────────────────────────────
const buildInitialState = (user: User | TeamMember, mode: EditUserModalMode): User | TeamMember => {
	if (mode === "team") {
		const tm = user as TeamMember;
		const base: TeamMember = {
			...tm,
			team_member_info: tm.team_member_info || defaultTeamMemberInfo,
			user_type: "clinical",
		};
		return base;
	}
	return { ...user };
};

// ── Component ──────────────────────────────────────────────────
const EditUserModal: React.FC<EditUserModalProps> = ({ isOpen, onClose, user, mode, onSaved }) => {
	const queryClient = useQueryClient();
	const [updatedUser, setUpdatedUser] = useState<User | TeamMember>(() => buildInitialState(user, mode));
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [errorMessages, setErrorMessages] = useState<string[]>([]);
	const [activeTab, setActiveTab] = useState("personal");
	const [roles, setRoles] = useState<Role[]>([]);
	const [availableRoles, setAvailableRoles] = useState<string[]>([]);
	const [passwordModalOpen, setPasswordModalOpen] = useState(false);
	const isPeptideOnly = hasFeature("healthcare.peptide") && !hasFeature("healthcare");

	// Re-initialise whenever the target user changes (e.g. opening a different record)
	useEffect(() => {
		setUpdatedUser(buildInitialState(user, mode));
		setErrorMessages([]);
		setActiveTab("personal");
	}, [user]);

	useEffect(() => {
		loadData();
	}, []);

	useEffect(() => {
		// One company per clinic DB — load the single clinic's available practitioner roles.
		fetchClinicCompany()
			.then((company: { id: number }) => fetchCompanyAvailableRolesPractitioner(company.id))
			.then(setAvailableRoles)
			.catch(() => setAvailableRoles([]));
	}, []);

	const loadData = async () => {
		try {
			const rolesData = await getAllRolesPractitioner();
			setRoles(rolesData);
		} catch (error) {
			console.error("Error loading modal data:", error);
		}
	};

	const persist = async (): Promise<User | TeamMember> => {
		let saved: User | TeamMember;
		if (mode === "admin") {
			saved = updatedUser.id !== ""
				? await updateUser(updatedUser.id, updatedUser as User)
				: await createUser(updatedUser as User);
		} else {
			saved = updatedUser.id !== ""
				? await updateTeamMember(updatedUser.id, updatedUser as TeamMember)
				: await createTeamMember(updatedUser as TeamMember);
		}
		setUpdatedUser(saved);
		return saved;
	};

	const handleSubmit = async () => {
		setIsSubmitting(true);
		try {
			onSaved(await persist());
		} catch (error) {
			handleErrors(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Blur-triggered save persists in the background — it must NOT call onSaved,
	// whose parent handlers close the modal. The saved record reaches the parent
	// on close via handleClose.
	const handleAutoSave = async () => {
		if (updatedUser.id === "") return;
		setIsSubmitting(true);
		try {
			await persist();
		} catch (error) {
			handleErrors(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Admin mode only: toggle activation status
	const handleToggleActive = async () => {
		if (updatedUser.id === "") { handleSubmit(); return; }
		setIsSubmitting(true);
		try {
			const isActive = updatedUser.is_active;
			isActive ? await deactivateUser(updatedUser.id) : await activateUser(updatedUser.id);
			const next = { ...updatedUser, is_active: !isActive };
			setUpdatedUser(next);
			onSaved(next);
		} catch (error) {
			handleErrors(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleErrors = (error: unknown) => {
		if (axios.isAxiosError(error)) {
			// healthcare's 422 seat refusal (same body as RolesTab's) carries used/total; show them and where to add seats.
			if (error.response?.status === 422 && error.response?.data?.error === "seat_limit_reached") {
				const { used, total } = error.response.data;
				setErrorMessages([
					`Seat limit reached (${used} of ${total} seats used). Contact support via the Help page to add seats.`,
				]);
				return;
			}
			// The colour palette shown to the operator is a snapshot from page load — another
			// admin can take the same swatch in between. Say so specifically (not a generic
			// "Save failed") and refetch the palette so the retry offers only free colours.
			// The rest of the form (updatedUser / errorMessages state) is left untouched — every
			// other edit stays intact for the retry.
			if (isPractitionerColorConflictError(error)) {
				setErrorMessages(["That colour was just taken by someone else. Choose another and save again."]);
				queryClient.invalidateQueries({ queryKey: PRACTITIONER_COLOR_PALETTE_QUERY_KEY });
				return;
			}
			if (error.response?.data?.errors) {
				const msgs = Object.entries(error.response.data.errors).flatMap(
					([, m]) => m as string[]
				);
				setErrorMessages(msgs);
			} else if (error.response?.data?.message) {
				setErrorMessages([error.response.data.message]);
			} else {
				setErrorMessages(["An unknown error occurred."]);
			}
		} else if (error instanceof Error) {
			setErrorMessages([error.message]);
		} else {
			setErrorMessages(["An unknown error occurred."]);
		}
	};

	const handleClose = () => {
		onSaved(updatedUser);
		onClose();
	};

	// ── Tab definitions ────────────────────────────────────────
	const adminTabs = [
		{
			key: "personal",
			label: "Personal Info",
			children: (
				<PersonalTab
					user={updatedUser}
					setUser={setUpdatedUser as React.Dispatch<React.SetStateAction<any>>}
					mode={mode}
					handleAutoSave={handleAutoSave}
				/>
			),
		},
		{
			key: "company",
			label: "Company Info",
			children: (
				<CompanyTab
					updatedUser={updatedUser as User}
					setUpdatedUser={setUpdatedUser as React.Dispatch<React.SetStateAction<any>>}
					handleAutoSave={handleAutoSave}
				/>
			),
		},
		{
			key: "preferences",
			label: "Preferences",
			children: <PreferencesTab />,
		},
		{
			key: "roles",
			label: "Roles",
			children: (
				<RolesTab
					user={updatedUser}
					setUser={setUpdatedUser as React.Dispatch<React.SetStateAction<any>>}
					roles={roles}
					availableRoles={availableRoles}
					mode={mode}
					handleAutoSave={handleAutoSave}
				/>
			),
		},
		{
			key: "samples",
			label: "Samples",
			children: (
				<SampleTab
					patient={updatedUser as User}
					isSubmitting={isSubmitting}
					setIsSubmitting={setIsSubmitting}
					setErrorMessages={setErrorMessages}
					handleAutoSave={handleAutoSave}
				/>
			),
		},
	];

	const teamTabs = [
		{
			key: "personal",
			label: "Personal Info",
			children: (
				<PersonalTab
					user={updatedUser}
					setUser={setUpdatedUser as React.Dispatch<React.SetStateAction<any>>}
					mode={mode}
					handleAutoSave={handleAutoSave}
				/>
			),
		},
		{
			key: "team_info",
			label: "Team Member Info",
			children: (
				<TeamMemberInfoTab
					updatedTeamMember={updatedUser as TeamMember}
					setUpdatedTeamMember={setUpdatedUser as React.Dispatch<React.SetStateAction<any>>}
					mode={mode}
				/>
			),
		},
		{
			key: "roles",
			label: "Company Roles",
			children: (
				<RolesTab
					user={updatedUser}
					setUser={setUpdatedUser as React.Dispatch<React.SetStateAction<any>>}
					roles={roles}
					availableRoles={availableRoles}
					mode={mode}
					handleAutoSave={handleAutoSave}
				/>
			),
		},
		{
			key: "education",
			label: "Education",
			children: (
				<EducationTab
					teamMember={updatedUser as TeamMember}
					setTeamMember={setUpdatedUser as React.Dispatch<React.SetStateAction<any>>}
				/>
			),
		},
		...(!isPeptideOnly ? [
			{
				key: "availability",
				label: "Availability & Billing",
				children: (
					<AvailabilityTab
						teamMember={updatedUser as TeamMember}
						setTeamMember={setUpdatedUser as React.Dispatch<React.SetStateAction<any>>}
					/>
				),
			},
		] : [])
	];

	const tabItems = mode === "admin" ? adminTabs : teamTabs;

	// ── Footer actions ──────────────────────────────────────────
	const footer = (
		<>
			{updatedUser.id !== "" && (
				<Button onClick={() => setPasswordModalOpen(true)} disabled={isSubmitting}>
					Reset Password
				</Button>
			)}
			{mode === "admin" && (
				<Button
					danger={updatedUser.is_active}
					type={updatedUser.is_active ? "default" : "primary"}
					onClick={handleToggleActive}
					loading={isSubmitting}
				>
					{updatedUser.is_active ? "Deactivate" : "Activate"}
				</Button>
			)}
		</>
	);

	// ── Render ─────────────────────────────────────────────────
	return (
		<>
			<DetailDrawer
				open={isOpen}
				onClose={handleClose}
				width={mode === "team" ? "85vw" : "70vw"}
				avatar={
					<Avatar
						size={44}
						src={updatedUser.profile_picture || "/assets/images/default-profile.png"}
						icon={<UserOutlined />}
					/>
				}
				title={
					updatedUser.id
						? `${updatedUser.first_name} ${updatedUser.last_name}`
						: mode === "team"
						? "Create New Team Member"
						: "Create New User"
				}
				subtitle={updatedUser.email}
				headerActions={
					<Button
						type="primary"
						icon={<SaveOutlined />}
						loading={isSubmitting}
						onClick={handleSubmit}
					>
						Save
					</Button>
				}
				tabs={tabItems}
				activeTab={activeTab}
				onTabChange={setActiveTab}
				errorMessages={errorMessages}
				onClearErrors={() => setErrorMessages([])}
				footer={footer}
			/>

			{passwordModalOpen && (
				<ResetPasswordModal
					isOpen={passwordModalOpen}
					onClose={() => setPasswordModalOpen(false)}
					user={updatedUser as User}
					handleErrors={handleErrors}
				/>
			)}
		</>
	);
};

export default EditUserModal;
